import type { Store } from '../data/stores';
import { entraEnModo, ModoTiendas } from './modoTiendas';
import { storeChainBranch } from '../data/stores';
import type { SucursalRow } from '../types/indicadores.types';
import type { ProcedenciaClientes } from './datosCierre';

/**
 * Totales + medidores del embudo operativo Global, agregados client-side a
 * partir de las filas de `GET /dashboard/sucursales` acotadas a
 * `filteredStores` (la cascada de `utils/filtros.ts`). Recreado de la lógica
 * de agregación del design handoff (`Tablero Colectivo Moda.dc.html`, líneas
 * 380-393): suma por tienda -> totales `C/S/A/V/N`, `efect=S/C`, `sla=A/S`,
 * `tasa=V/A` (V ÷ A desde el ítem 8 del 28-jul; el comentario decía V/S).
 *
 * `efect`/`sla`/`tasa` viajan como fracción 0..1 (o `null` si el denominador
 * es 0) para que `fmtPct`/`ring` (ya null-safe) los formateen sin duplicar la
 * lógica de "—"/0% aquí.
 *
 * `ventaUnavailable` es `true` solo cuando HAY filas unidas y TODAS traen
 * `paresVendidos` en `null` (Proscai caído durante todo el rango) — el
 * embudo debe mostrar "N/D" en vez de "0" para no confundir "sin venta" con
 * "no se pudo consultar la venta".
 */
export interface GlobalAggregate {
  C: number;
  S: number;
  A: number;
  N: number;
  V: number;
  /**
   * Desglose de la Venta agregada (2026-07-28, punto 4 — cards globales de
   * venta). `vContadoPares`/`vCreditoPares` en pares; `vContadoMonto`/
   * `vCreditoMonto` en $ (bruto del ticket, DBRUTO por documento). Suman solo
   * las filas con dato (mismo criterio null-safe que `V`).
   */
  vContadoPares: number;
  vCreditoPares: number;
  vContadoMonto: number;
  vCreditoMonto: number;
  /** Descuento concedido: lo que separa esta venta de la del reporte de negocio. */
  vDescuento: number;
  /** Préstamos personales: NO son venta comercial. */
  vPrestamos: number;
  /**
   * Clientes nuevos A CRÉDITO agregados de las filas unidas (2026-07-28). Suma
   * solo las filas con dato. `clientesNuevosUnavailable` = true cuando HAY
   * filas pero TODAS traen `clientesNuevos` null (query lenta de Proscai sin
   * responder) → la card muestra "Calculando…"/"N/D" en vez de un 0 falso.
   *
   * `clientesNuevosConVenta` = créditos ESTRENADOS (regla B, 5-oct-2026): cada
   * cliente cae en UNA tienda (la de su primer ticket a crédito) y en UN día,
   * así que sumar las filas no duplica. Puede salir mayor que `clientesNuevos`.
   */
  clientesNuevos: number;
  clientesNuevosConVenta: number;
  /**
   * `true` cuando la API avisa que la consulta de altas NO respondió
   * (`clientesNuevosPendiente` en alguna fila) -> «calculando…». Antes se
   * deducía de que TODAS las filas trajeran `clientesNuevos` en null, y una
   * cadena sin altas (TRE no maneja crédito) se quedaba en «calculando…» para
   * siempre, con 12 reintentos de `/sucursales` detrás.
   */
  clientesNuevosPendiente: boolean;
  efect: number | null;
  sla: number | null;
  tasa: number | null;
  ventaUnavailable: boolean;
  /**
   * Tickets de la cascada (SPEC 2026-10-13, punto d), préstamos incluidos.
   * `undefined` = API anterior (ningún renglón trae el campo): no se pinta;
   * `null` = algún renglón no los sabe («N/D»), nunca una suma a medias.
   */
  tickets?: number | null;
  ticketsPrestamo?: number | null;
  /**
   * Procedencia de las altas (SPEC 2026-10-13, punto c). `undefined` = API
   * anterior. La deriva es la suma de la de los renglones de la cascada; «el
   * correo dijo» es un total global y sólo se da sin cascada.
   */
  procedenciaClientes?: ProcedenciaClientes;
}

/**
 * Clave de join `(chainId, branchOfficeId)` usada tanto para filas de la API
 * como para `Store` del catálogo. Exportada para que `utils/tabla.ts` (Task
 * 7, tabla por sucursal) haga EXACTAMENTE el mismo join en vez de duplicar
 * el formato de la clave.
 */
export function rowKey(chainId: string, branchOfficeId: number): string {
  return `${chainId}|${branchOfficeId}`;
}

/**
 * Une `rows` (respuesta cruda de `/dashboard/sucursales`, SIN acotar) con
 * `filteredStores` (el subconjunto que dejó la cascada Regional/Supervisor/
 * Cadena/Tienda) por `(chain, branchOffice)` y agrega los totales + los 3
 * medidores del embudo Global.
 *
 * ⚠️ EL MODO SÓLO ACOTA LA OPERACIÓN (decisión del usuario, 12-oct). `modo`
 * (switch «Activas / Sin uso / Todas» del Header, default `'todas'` aquí) filtra
 * por `atendidas` las cifras OPERATIVAS: C, S, A, N y los tres medidores. La
 * VENTA (pares, montos, descuento, préstamos), los clientes nuevos y los
 * estrenos son SIEMPRE los de la cascada, sin modo.
 *
 * Antes el modo de fábrica («Activas») tiraba la fila ENTERA de las tiendas sin
 * atendidas, venta incluida: la S40 salía con $29.972.988,59 contra los
 * $30.140.592,86 del reporte semanal, porque ADUANA y VAZ 247 vendieron sin usar
 * CADER. Con la venta fuera del modo, Global cuadra con el reporte en cualquier
 * modo.
 *
 * La TASA es un medidor y por eso usa el universo del modo en los dos términos
 * (V del modo ÷ A del modo): mezclar la venta de todas con las atendidas de unas
 * pocas inflaría el cociente con tiendas que no usan la herramienta. Por eso
 * `V` (embudo) y el numerador de `tasa` pueden diferir fuera de «todas».
 */
export function aggregateGlobal(
  rows: SucursalRow[],
  filteredStores: Store[],
  modo: ModoTiendas = 'todas',
  /**
   * `true` con la cascada vacía («Todas», SPEC 2026-10-13, punto b): entran
   * TODOS los renglones de la API, también los de una tienda que ningún
   * catálogo ubica. Invariante: Global sin cascada = suma de los renglones.
   */
  sinCascada = false,
): GlobalAggregate {
  const allowedKeys = new Set(
    filteredStores.map((s) => {
      const { chain, branchOffice } = storeChainBranch(s);
      return rowKey(chain, branchOffice);
    }),
  );

  // La cascada decide la venta; la cascada + el modo, la operación.
  const enCascada = sinCascada
    ? rows
    : rows.filter((r) => allowedKeys.has(rowKey(r.chainId, r.branchOfficeId)));
  const enModo = enCascada.filter((r) => entraEnModo(r.atendidas, modo));

  let S = 0;
  let A = 0;
  let N = 0;
  let vModo = 0;
  for (const row of enModo) {
    S += row.solicitudes;
    A += row.atendidas;
    N += row.negados;
    if (row.paresVendidos !== null && row.paresVendidos !== undefined) {
      vModo += row.paresVendidos;
    }
  }

  let V = 0;
  let vContadoPares = 0;
  let vCreditoPares = 0;
  let vContadoMonto = 0;
  let vCreditoMonto = 0;
  let vDescuento = 0;
  let vPrestamos = 0;
  let clientesNuevos = 0;
  let clientesNuevosConVenta = 0;

  for (const row of enCascada) {
    if (row.paresVendidos !== null && row.paresVendidos !== undefined) {
      V += row.paresVendidos;
    }
    vContadoPares += row.ventaContadoPares ?? 0;
    vCreditoPares += row.ventaCreditoPares ?? 0;
    vContadoMonto += row.ventaContadoMonto ?? 0;
    vCreditoMonto += row.ventaCreditoMonto ?? 0;
    vDescuento += row.ventaDescuento ?? 0;
    vPrestamos += row.ventaPrestamos ?? 0;
    clientesNuevos += row.clientesNuevos ?? 0;
    clientesNuevosConVenta += row.clientesNuevosConVenta ?? 0;
  }

  // La bandera de la API manda (contrato del 12-oct): `clientesNuevos` llega
  // en 0 cuando la consulta respondió sin filas y en null SÓLO si no respondió.
  const clientesNuevosPendiente = enCascada.some((r) => r.clientesNuevosPendiente === true);

  const C = S + N;
  const efect = C > 0 ? S / C : null;
  const sla = S > 0 ? A / S : null;
  // Ítem 8 (2026-07-28): "Tasa de atención" = venta ÷ solicitudes ATENDIDAS
  // (antes ÷ solicitudes). Alinea la gauge global con la `tasaAtencion` por
  // vendedor (vendido÷surtido). null si no hubo atendidas (evita /0). Los dos
  // términos del universo del modo (ver el comentario de la función).
  const tasa = A > 0 ? vModo / A : null;

  const ventaUnavailable =
    enCascada.length > 0 &&
    enCascada.every((r) => r.paresVendidos === null || r.paresVendidos === undefined);

  // Tickets (SPEC 2026-10-13, d): sólo si la API los manda; con un renglón que
  // no los sabe, «N/D» para todos.
  const conCampo = rows.some((r) => r.tickets !== undefined);
  let tickets: number | null | undefined;
  let ticketsPrestamo: number | null | undefined;
  if (conCampo) {
    const sabidos = enCascada.every((r) => r.tickets !== null && r.tickets !== undefined);
    tickets = sabidos ? enCascada.reduce((a, r) => a + (r.tickets ?? 0), 0) : null;
    ticketsPrestamo = sabidos ? enCascada.reduce((a, r) => a + (r.ticketsPrestamo ?? 0), 0) : null;
  }

  // Procedencia de las altas (SPEC 2026-10-13, c): igual en todos los renglones.
  const muestra = rows.find((r) => r.clientesNuevosFuente !== undefined);
  const procedenciaClientes: ProcedenciaClientes | undefined = muestra?.clientesNuevosFuente
    ? {
        fuente: muestra.clientesNuevosFuente,
        cierre: muestra.clientesNuevosCierre ?? null,
        deriva: enCascada.some((r) => r.clientesNuevosDeriva === null || r.clientesNuevosDeriva === undefined)
          ? null
          : enCascada.reduce((a, r) => a + (r.clientesNuevosDeriva ?? 0), 0),
        correo: sinCascada ? muestra.clientesNuevosCorreo ?? null : null,
      }
    : undefined;

  return {
    C,
    S,
    A,
    N,
    V,
    vContadoPares,
    vCreditoPares,
    vContadoMonto,
    vCreditoMonto,
    vDescuento,
    vPrestamos,
    clientesNuevos,
    clientesNuevosConVenta,
    clientesNuevosPendiente,
    efect,
    sla,
    tasa,
    ventaUnavailable,
    tickets,
    ticketsPrestamo,
    procedenciaClientes,
  };
}

/** `Date` -> `YYYY-MM-DD` en hora LOCAL (nunca `toISOString`, que trunca a UTC). */
export function isoDateLocal(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export interface DateRange {
  desde: string;
  hasta: string;
}

/**
 * Semana calendario ANTERIOR a la de `today` (lunes 00:00 a domingo),
 * ambos extremos en `YYYY-MM-DD` hora local. `daysFromMonday` normaliza
 * `Date#getDay()` (0=domingo) a "días desde el lunes de ESTA semana"
 * (0=lunes .. 6=domingo) para poder retroceder al lunes de la semana
 * anterior sin importar en qué día de la semana caiga `today`.
 */
export function previousWeekRange(today: Date = new Date()): DateRange {
  const daysFromMonday = (today.getDay() + 6) % 7;
  const thisMonday = new Date(today);
  thisMonday.setDate(thisMonday.getDate() - daysFromMonday);
  const prevMonday = new Date(thisMonday);
  prevMonday.setDate(prevMonday.getDate() - 7);
  const prevSunday = new Date(prevMonday);
  prevSunday.setDate(prevSunday.getDate() + 6);
  return { desde: isoDateLocal(prevMonday), hasta: isoDateLocal(prevSunday) };
}

/**
 * Primer y último día del mes calendario ANTERIOR al de `today` (ambos
 * extremos en `YYYY-MM-DD` hora local). `new Date(y, m, 0)` es un truco
 * estándar de JS: el "día 0" del mes `m` es el último día del mes `m-1`.
 */
export function previousMonthRange(today: Date = new Date()): DateRange {
  const y = today.getFullYear();
  const m = today.getMonth();
  const desde = new Date(y, m - 1, 1);
  const hasta = new Date(y, m, 0);
  return { desde: isoDateLocal(desde), hasta: isoDateLocal(hasta) };
}

/**
 * Rango default del fetch Global: la semana calendario ANTERIOR completa
 * (lunes a domingo, ver `previousWeekRange`) — feedback del PO: "toda la
 * semana anterior" da un periodo cerrado y comparable, a diferencia de una
 * ventana móvil de N días. El screen lo calcula una sola vez al montar — la
 * cascada de filtros filtra client-side sin refetch; los botones rápidos de
 * `Filtros` ("Semana anterior"/"Mes anterior") permiten recalcularlo a mano.
 */
export function defaultDateRange(today: Date = new Date()): DateRange {
  return previousWeekRange(today);
}

/**
 * `{desde,hasta}` (`YYYY-MM-DD`) -> etiqueta corta estilo handoff
 * (`"15/06 – 31/07/2026"`, líneas 44/58 de `Tablero Colectivo Moda.dc.html`):
 * día/mes de cada extremo, año una sola vez al final (el de `hasta`).
 */
export function formatDateRangeLabel(range: DateRange): string {
  const [, dm, dd] = range.desde.split('-');
  const [hy, hm, hd] = range.hasta.split('-');
  return `${dd}/${dm} – ${hd}/${hm}/${hy}`;
}
